import { useMemo } from 'react';
import { motion } from 'framer-motion';
import type { JarticardSobre } from '@/lib/types';
import { Coins, Sparkles } from 'lucide-react';

/** Aclara/oscurece un hex en una cantidad (-255..255). */
function shade(hex: string, amt: number) {
  const h = hex.replace('#', '');
  const num = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  let r = (num >> 16) + amt, g = ((num >> 8) & 0xff) + amt, b = (num & 0xff) + amt;
  r = Math.max(0, Math.min(255, r)); g = Math.max(0, Math.min(255, g)); b = Math.max(0, Math.min(255, b));
  return `rgb(${r}, ${g}, ${b})`;
}

export function BoosterPack({ sobre, onBuy, delay = 0, soldOut = false, stockLabel }: { sobre: JarticardSobre; onBuy: () => void; delay?: number; soldOut?: boolean; stockLabel?: string }) {
  const color = sobre.color || '#f59e0b';
  const light = useMemo(() => shade(color, 60), [color]);

  // Destellos dispersos
  const twinkles = useMemo(
    () => Array.from({ length: 6 }, (_, i) => ({
      id: i,
      top: `${10 + Math.random() * 70}%`,
      left: `${8 + Math.random() * 84}%`,
      size: 3 + Math.random() * 5,
      delay: Math.random() * 2,
    })),
    [],
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="group relative [perspective:1200px]"
    >
      {/* Halo de color */}
      <div
        className="pointer-events-none absolute -inset-3 rounded-[2rem] opacity-40 blur-2xl transition-opacity duration-300 group-hover:opacity-90"
        style={{ background: `radial-gradient(circle at 50% 40%, ${color}, transparent 70%)` }}
      />

      <motion.button
        onClick={onBuy}
        disabled={soldOut}
        whileHover={soldOut ? undefined : { y: -10, rotateX: 6, rotateY: -4, scale: 1.03 }}
        whileTap={soldOut ? undefined : { scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 300, damping: 18 }}
        className="relative block aspect-[3/4] w-full overflow-hidden rounded-2xl text-left [transform-style:preserve-3d] disabled:cursor-not-allowed"
        style={{
          background: `linear-gradient(160deg, ${light} 0%, ${color} 35%, #0d0e14 100%)`,
          boxShadow: `0 18px 40px -12px ${color}cc, inset 0 0 0 2px rgba(255,255,255,0.18), inset 0 0 0 4px ${shade(color, -60)}`,
          filter: soldOut ? 'grayscale(0.85) brightness(0.6)' : undefined,
        }}
      >
        {/* Film holográfico giratorio */}
        <div
          className="pointer-events-none absolute -inset-1/4 opacity-25 mix-blend-screen"
          style={{
            background: 'conic-gradient(from 0deg, #f43f5e, #f59e0b, #fde047, #4ade80, #38bdf8, #a855f7, #f43f5e)',
            animation: 'jc-rainbow 6s linear infinite',
            backgroundSize: '300% 300%',
          }}
        />

        {/* Barrido de brillo (más intenso al pasar el ratón) */}
        <span className="jc-holo-overlay opacity-30 transition-opacity duration-300 group-hover:opacity-70" />

        {/* Textura de líneas diagonales sutiles */}
        <div
          className="pointer-events-none absolute inset-0 opacity-10"
          style={{ background: 'repeating-linear-gradient(45deg, #fff 0 1px, transparent 1px 9px)' }}
        />

        {/* Tira de apertura superior */}
        <div className="absolute inset-x-0 top-0 z-10 flex h-9 items-center justify-center border-b border-dashed border-white/30 bg-black/25 backdrop-blur-[1px]">
          <span className="text-[0.6rem] font-black uppercase tracking-[0.45em] text-white/90">JartiCards</span>
        </div>

        {/* Destellos */}
        {twinkles.map((t) => (
          <motion.span
            key={t.id}
            className="pointer-events-none absolute rounded-full bg-white"
            style={{ top: t.top, left: t.left, width: t.size, height: t.size, boxShadow: '0 0 6px #fff' }}
            animate={{ opacity: [0, 1, 0], scale: [0.5, 1.3, 0.5] }}
            transition={{ repeat: Infinity, duration: 2.2, delay: t.delay }}
          />
        ))}

        {/* Medallón central */}
        <div className="absolute inset-0 flex items-center justify-center pt-6">
          <motion.div
            animate={{ y: [0, -6, 0] }}
            transition={{ repeat: Infinity, duration: 3.5, ease: 'easeInOut' }}
            className="relative grid place-items-center"
          >
            <div className="absolute h-32 w-32 rounded-full blur-xl" style={{ background: `${light}aa` }} />
            <div
              className="relative grid h-28 w-28 place-items-center overflow-hidden rounded-full border-2"
              style={{ borderColor: 'rgba(255,255,255,0.55)', background: `radial-gradient(circle, ${shade(color, -20)}, #0b0b10)` }}
            >
              {sobre.imageUrl ? (
                <img src={sobre.imageUrl} alt={sobre.name} className="h-full w-full object-cover" draggable={false} />
              ) : (
                <Sparkles className="h-12 w-12 text-white/85" />
              )}
            </div>
          </motion.div>
        </div>

        {/* Placa de nombre */}
        <div className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/90 via-black/55 to-transparent px-3 pb-3 pt-8 text-center">
          <p className="truncate font-headline text-base font-extrabold text-white drop-shadow">{sobre.name}</p>
          <p className="text-[0.7rem] font-medium uppercase tracking-wider text-white/70">{sobre.numCards} carta(s)</p>
        </div>

        {/* Chip de precio */}
        <div
          className="absolute right-2 top-11 z-20 flex items-center gap-1 rounded-full border border-amber-200/60 px-2.5 py-1 text-sm font-extrabold text-amber-950 shadow-lg"
          style={{ background: 'linear-gradient(160deg, #fde68a, #f59e0b)' }}
        >
          <Coins className="h-3.5 w-3.5" /> {sobre.price}
        </div>

        {/* Stock restante por usuario */}
        {stockLabel && !soldOut && (
          <div className="absolute left-2 top-11 z-20 rounded-full border border-white/30 bg-black/45 px-2 py-0.5 text-[0.65rem] font-bold text-white backdrop-blur-sm">
            {stockLabel}
          </div>
        )}

        {/* Agotado */}
        {soldOut && (
          <div className="absolute inset-0 z-30 grid place-items-center">
            <span className="-rotate-12 rounded-md border-2 border-white/80 bg-rose-700/90 px-4 py-1.5 text-lg font-black uppercase tracking-widest text-white shadow-xl">
              Agotado
            </span>
          </div>
        )}
      </motion.button>
    </motion.div>
  );
}

export default BoosterPack;
